import { useState, type RefObject } from "react";
import { Button, CircularProgress, ListItemIcon, Menu, MenuItem, Snackbar } from "@mui/material";
import ShareIcon from "@mui/icons-material/Share";
import ImageIcon from "@mui/icons-material/Image";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { toBlob } from "html-to-image";
import { jsPDF } from "jspdf";

/** Attributo da mettere sugli elementi da escludere dall'immagine (bottoni, icone di modifica, link) */
export const SHARE_HIDE = { "data-share-hide": "" };

interface Props {
    /** sezione da esportare: viene catturata per intero, anche la parte fuori dallo scorrimento */
    targetRef: RefObject<HTMLElement | null>;
    /** nome dei file senza estensione */
    fileName: string;
    title?: string;
}

async function capture(node: HTMLElement): Promise<Blob> {
    const blob = await toBlob(node, {
        backgroundColor: "#ffffff",
        pixelRatio: 2,
        filter: (n) => !(n instanceof HTMLElement && n.dataset.shareHide !== undefined),
    });
    if (!blob) throw new Error("immagine non generata");
    return blob;
}

function download(blob: Blob, name: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function loadImage(blob: Blob): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = URL.createObjectURL(blob);
    });
}

/** PDF a pagina unica larga come un A4 e lunga quanto la scheda: nessun taglio, comodo da scorrere sul telefono */
async function toPdf(blob: Blob): Promise<Blob> {
    const img = await loadImage(blob);
    const margin = 8;
    const width = 210 - margin * 2;
    const height = (img.height * width) / img.width;
    const pdf = new jsPDF({ unit: "mm", format: [210, height + margin * 2] });
    // JPEG invece del PNG: file leggero da mandare su WhatsApp
    const canvas = document.createElement("canvas");
    canvas.width = img.width;
    canvas.height = img.height;
    canvas.getContext("2d")!.drawImage(img, 0, 0);
    pdf.addImage(canvas.toDataURL("image/jpeg", 0.85), "JPEG", margin, margin, width, height, undefined, "FAST");
    URL.revokeObjectURL(img.src);
    return pdf.output("blob");
}

/** Bottone "Condividi": condivisione di sistema (WhatsApp dal telefono), oppure download come immagine o PDF */
export default function ShareSnapshotButton({ targetRef, fileName, title }: Props) {
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const run = async (kind: "share" | "png" | "pdf") => {
        setAnchor(null);
        if (!targetRef.current) return;
        setBusy(true);
        try {
            const png = await capture(targetRef.current);
            if (kind === "share") {
                const file = new File([png], `${fileName}.png`, { type: "image/png" });
                await navigator.share({ files: [file], title });
            } else if (kind === "png") {
                download(png, `${fileName}.png`);
            } else {
                download(await toPdf(png), `${fileName}.pdf`);
            }
        } catch (e: any) {
            if (e?.name !== "AbortError") setError("Non è stato possibile generare il file");
        } finally {
            setBusy(false);
        }
    };

    const canShareFiles = typeof navigator !== "undefined" && !!navigator.canShare
        && navigator.canShare({ files: [new File([""], "x.png", { type: "image/png" })] });

    return (
        <>
            <Button variant="outlined" size="small" disabled={busy} {...SHARE_HIDE}
                startIcon={busy ? <CircularProgress size={16} /> : <ShareIcon />}
                onClick={(e) => setAnchor(e.currentTarget)}>
                Condividi
            </Button>
            <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
                {canShareFiles && (
                    <MenuItem onClick={() => run("share")}>
                        <ListItemIcon><ShareIcon fontSize="small" /></ListItemIcon>Condividi (WhatsApp…)
                    </MenuItem>
                )}
                <MenuItem onClick={() => run("png")}>
                    <ListItemIcon><ImageIcon fontSize="small" /></ListItemIcon>Scarica immagine
                </MenuItem>
                <MenuItem onClick={() => run("pdf")}>
                    <ListItemIcon><PictureAsPdfIcon fontSize="small" /></ListItemIcon>Scarica PDF
                </MenuItem>
            </Menu>
            <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError(null)} message={error} />
        </>
    );
}
